/**
 * Phase 3 — Fuel Intelligence module pages.
 * Single file exporting 8 sub-pages.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Truck, Wrench, Activity, Droplet, AlertTriangle, UsersRound, PieChart, FileText,
  Plus, X, Loader2, Gauge, Fuel, Navigation2, Download, TrendingUp, Car,
  ZoomIn, ExternalLink,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel, api } from '../../lib/api';
import { formatINR, formatDateTime as _fmtDT } from '../../lib/format';
import { ProjectStrictPicker } from '../../components/pickers/ProjectClientPickers';
import LeafletMap from '../../components/LeafletMap';

const GPS_STATUS_STYLE = {
  unconfigured: 'bg-slate-100 text-slate-500 border-slate-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  offline: 'bg-amber-50 text-amber-700 border-amber-200',
  inactive: 'bg-red-50 text-red-700 border-red-200',
};

const GpsStatusBadge = ({ status }) => (
  <span data-testid="gps-status-badge"
    className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider border ${GPS_STATUS_STYLE[status] || GPS_STATUS_STYLE.unconfigured}`}>
    {status || 'unconfigured'}
  </span>
);

const GpsSection = ({ form, set, existing }) => {
  const inp = 'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm';
  const lastSeen = existing?.gps_last_seen
    ? _fmtDT(existing.gps_last_seen)
    : '—';
  const hasPos = existing?.gps_lat != null && existing?.gps_lng != null;
  return (
    <div className="col-span-full mt-2 border-t border-slate-200 pt-3" data-testid="gps-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-display font-bold text-sm flex items-center gap-1.5"><Navigation2 size={14} className="text-[#3b4872]" /> GPS Tracking</h4>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!form.gps_enabled} onChange={(e) => set('gps_enabled', e.target.checked)} data-testid="gps-enabled-toggle" />
          <span className="text-xs font-display font-bold uppercase tracking-wider text-slate-600">{form.gps_enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>
      {form.gps_enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Device ID (IMEI / Serial)"><input value={form.gps_device_id || ''} onChange={(e) => set('gps_device_id', e.target.value)} placeholder="e.g. 86xxxxxxxxxxxxx" className={inp} data-testid="gps-device-id" /></Field>
          <Field label="Provider"><input value={form.gps_provider || ''} onChange={(e) => set('gps_provider', e.target.value)} placeholder="e.g. Trakzee, Letstrack — configure later" className={inp} data-testid="gps-provider" /></Field>
          <Field label="SIM Number"><input value={form.gps_sim_number || ''} onChange={(e) => set('gps_sim_number', e.target.value)} placeholder="SIM in the GPS device" className={inp} data-testid="gps-sim" /></Field>
          <Field label="Status">
            <div className="flex items-center gap-3 py-1.5">
              <GpsStatusBadge status={existing?.gps_status || (form.gps_device_id ? 'inactive' : 'unconfigured')} />
              <span className="text-xs text-slate-500">Last seen: {lastSeen}</span>
            </div>
          </Field>
          {form.gps_enabled && !(form.gps_device_id || '').trim() && (
            <div className="col-span-full text-xs text-amber-600">Enter Device ID to activate GPS tracking.</div>
          )}
          {hasPos && (
            <div className="col-span-full">
              <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 mb-1">Last Known Location</div>
              <LeafletMap markers={[{ id: existing.id, lat: existing.gps_lat, lng: existing.gps_lng, color: '#3b4872', label: form.vehicle_number }]} height="200px" zoom={14} center={[existing.gps_lat, existing.gps_lng]} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FUEL_TYPES = ['Diesel', 'Petrol', 'CNG', 'Electric', 'LPG'];
const VEHICLE_TYPES = ['Car', 'SUV', 'Pickup', 'Truck', 'Tipper', 'Concrete Mixer', 'Water Tanker', 'Bus', 'Other'];
const EQUIPMENT_TYPES = ['Excavator', 'Crane', 'Tower Crane', 'Concrete Pump', 'Batching Plant', 'Compressor', 'Generator (DG Set)', 'Boom Lift', 'Roller', 'Grader', 'Dozer', 'Other'];

const Tile = ({ label, value, sub, accent = 'slate' }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-display">{label}</div>
    <div className={`text-2xl font-bold mt-1 text-${accent}-700`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-display text-slate-500">{label}</label>
    {children}
  </div>
);


// ────────────────────────────────────────────────────────────
// 2.1 Dashboard
// ────────────────────────────────────────────────────────────
export function FuelDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { apiGet('/fuel/dashboard').then(setD).catch(() => {}); }, []);
  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;

  const CHART_COLORS = ['#3b4872','#6366f1','#f59e0b','#10b981','#ef4444','#8b5cf6'];
  const totalLitres = (d.project_consumption || []).reduce((s, p) => s + (p.litres || 0), 0);

  return (
    <div className="p-6 space-y-5">
      <PageHeader title="Fuel Dashboard" subtitle="Vehicle + equipment consumption across all projects" icon={<Fuel size={18} />} />

      {/* ── Enhanced KPI Strip (8 tiles — matches Excel Dashboard) ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Tile label="Diesel Received" value={`${(d.diesel_received_l || 0).toFixed(0)} L`} accent="blue" />
        <Tile label="Fuel · This Month" value={`${d.this_month_litres} L`} accent="amber" />
        <Tile label="Cost · This Month" value={formatINR(d.this_month_cost)} sub={`${d.cost_delta_pct >= 0 ? '+' : ''}${d.cost_delta_pct}% vs last month`} accent="indigo" />
        <Tile label="Avg Rate ₹/L" value={`₹${(d.avg_rate_per_litre || 0).toFixed(2)}`} accent="slate" />
        <Tile label="Stock Shortage" value={formatINR(d.stock_shortage_inr || 0)} sub={`${(d.stock_shortage_l || 0).toFixed(1)} L`} accent="rose" />
        <Tile label="Excess Fuel" value={formatINR(d.excess_fuel_inr || 0)} sub={`${(d.excess_fuel_l || 0).toFixed(1)} L`} accent="amber" />
        <Tile label="Diesel at Risk" value={formatINR(d.diesel_at_risk_inr || 0)} accent="red" />
        <Tile label="Red Flags" value={d.red_flags || 0} accent={d.red_flags > 0 ? 'rose' : 'green'} sub={d.red_flags > 0 ? 'exceptions' : 'all clear'} />
      </div>

      {/* ── Daily Trend (last 7 days) ── */}
      {(d.daily_trend || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2">
            <TrendingUp size={14} className="text-[#3b4872]" /> Daily Consumption · Last 7 Days
          </h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={d.daily_trend} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
              <XAxis dataKey="_id" tick={{ fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} unit="L" />
              <Tooltip formatter={(v) => [`${v.toFixed(1)} L`, 'Litres']} labelFormatter={l => `Date: ${l}`} />
              <Bar dataKey="litres" fill="#3b4872" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Project + Equipment ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3">Project Consumption · This Month</h3>
          <div className="space-y-2">
            {(d.project_consumption || []).filter(p => p.litres > 0).slice(0, 8).map((p, i) => {
              const pct = totalLitres > 0 ? Math.round((p.litres / totalLitres) * 100) : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className="truncate max-w-[60%] font-medium">{p.project_name || '—'}</span>
                    <span className="tabular-nums text-slate-600">{p.litres?.toFixed(1)} L · {formatINR(p.amount)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full">
                    <div className="h-1.5 rounded-full bg-[#3b4872]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {!(d.project_consumption || []).some(p => p.litres > 0) && (
              <div className="text-xs text-slate-400 italic">No logs yet this month.</div>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3">Top 5 High-Consumption Equipment</h3>
          <div className="space-y-1.5">
            {(d.top_equipment || []).map((e, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span>#{i + 1} {e.name || e._id || '—'}</span>
                <span className="font-bold tabular-nums">{e.litres?.toFixed(1)} L</span>
              </div>
            ))}
            {!d.top_equipment?.length && <div className="text-xs text-slate-400 italic">No equipment logs.</div>}
          </div>
        </div>
      </div>

      {/* ── Top Vehicles + Fuel Type Breakdown ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2">
            <Car size={13} className="text-[#3b4872]" /> Top Vehicles · This Month
          </h3>
          <div className="space-y-1.5">
            {(d.top_vehicles || []).map((v, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate max-w-[60%]">#{i + 1} {v.vehicle_name}</span>
                <span className="tabular-nums text-slate-600">{v.litres?.toFixed(1)} L · {formatINR(v.amount)}</span>
              </div>
            ))}
            {!d.top_vehicles?.length && <div className="text-xs text-slate-400 italic">No vehicle logs this month.</div>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3">Fuel Type Breakdown · This Month</h3>
          {(d.fuel_type_breakdown || []).length > 0 ? (
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={d.fuel_type_breakdown} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 10 }} unit="L" />
                    <YAxis type="category" dataKey="_id" tick={{ fontSize: 10 }} width={55} />
                    <Tooltip formatter={(v) => [`${v.toFixed(1)} L`, 'Litres']} />
                    <Bar dataKey="litres" radius={[0, 3, 3, 0]}>
                      {d.fuel_type_breakdown.map((_, idx) => (
                        <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic">No logs this month.</div>
          )}
        </div>
      </div>

      {/* ── Tank Levels ── */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><Droplet size={14} className="text-cyan-500" /> Tank Levels</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(d.tanks || []).map((t) => {
            const pct = t.capacity_l ? Math.min(100, Math.round((t.current_level_l / t.capacity_l) * 100)) : 0;
            const color = pct < 25 ? 'bg-rose-500' : pct < 50 ? 'bg-amber-500' : 'bg-emerald-500';
            return (
              <div key={t.id} className="border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest font-display text-slate-500">{t.tank_id}</div>
                <div className="text-xs truncate">{t.project_name}</div>
                <div className="mt-2 h-3 bg-slate-100 rounded">
                  <div className={`h-3 ${color} rounded`} style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-1 text-xs flex justify-between"><span>{t.current_level_l?.toFixed(0)} L</span><span className="text-slate-400">/ {t.capacity_l?.toFixed(0)} L</span></div>
              </div>
            );
          })}
          {!d.tanks?.length && <div className="text-xs text-slate-400 italic col-span-4">No tanks configured.</div>}
        </div>
      </div>

      {/* ── Diesel Theft Control ── */}
      {(d.red_flags > 0 || d.diesel_at_risk_inr > 0) ? (
        <div className="border-2 border-red-200 rounded-sm p-4 bg-red-50" data-testid="theft-control-section">
          <h3 className="font-display font-bold text-sm text-red-800 mb-3 flex items-center gap-2">
            <span className="text-base">&#9888;</span> Diesel Theft Control — Exception Report
          </h3>
          {/* Tank Reconciliation Table */}
          {(d.tank_reconciliation || []).length > 0 && (
            <div className="mb-4">
              <h4 className="font-display font-bold text-xs uppercase tracking-wider text-slate-600 mb-2">
                A. Stock Reconciliation — by Tank
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#3b4872] text-white">
                      <th className="px-3 py-2 text-left">Tank</th>
                      <th className="px-3 py-2 text-left">Project</th>
                      <th className="px-3 py-2 text-right">Book Stock (L)</th>
                      <th className="px-3 py-2 text-right">Physical Dip (L)</th>
                      <th className="px-3 py-2 text-right">Variance (L)</th>
                      <th className="px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.tank_reconciliation || []).map((t, i) => (
                      <tr key={i} className={`border-t border-slate-200 ${t.status === '⚠ INVESTIGATE' ? 'bg-red-50' : ''}`}>
                        <td className="px-3 py-2 font-medium">{t.tank_name}</td>
                        <td className="px-3 py-2 text-slate-500">{t.project_name || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{(t.book_stock_l || 0).toFixed(1)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{(t.physical_dip_l || 0).toFixed(1)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${t.variance_l < 0 ? 'text-red-600' : t.variance_l > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {t.variance_l >= 0 ? '+' : ''}{(t.variance_l || 0).toFixed(1)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.status === '⚠ INVESTIGATE' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {t.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(d.tank_reconciliation || []).some(t => t.status === '⚠ INVESTIGATE') && (
                <p className="text-[11px] text-red-700 mt-2 font-medium">
                  ⚠ Cross-check the operator log, gate register and CCTV for flagged tanks.
                </p>
              )}
            </div>
          )}
          {/* Equipment Over-Consumption Table */}
          {(d.equipment_efficiency || []).filter(e => e.status !== 'OK' && e.total_litres > 0).length > 0 && (
            <div>
              <h4 className="font-display font-bold text-xs uppercase tracking-wider text-slate-600 mb-2">
                B. Equipment Over-Consumption — This Month
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#3b4872] text-white">
                      <th className="px-3 py-2 text-left">Equipment</th>
                      <th className="px-3 py-2 text-left">Project</th>
                      <th className="px-3 py-2 text-right">Actual L/hr</th>
                      <th className="px-3 py-2 text-right">Std L/hr</th>
                      <th className="px-3 py-2 text-right">Var %</th>
                      <th className="px-3 py-2 text-right">Excess (L)</th>
                      <th className="px-3 py-2 text-right">Excess (₹)</th>
                      <th className="px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.equipment_efficiency || []).filter(e => e.total_litres > 0).slice(0, 15).map((e, i) => (
                      <tr key={i} className={`border-t border-slate-200 ${e.status === '⚠ OVER' ? 'bg-amber-50' : ''}`}>
                        <td className="px-3 py-2 font-medium">{e.equipment_name}</td>
                        <td className="px-3 py-2 text-slate-500">{e.project_name || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{(e.actual_per_unit || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{(e.std_per_unit || 0).toFixed(2)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${e.variance_pct > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {e.variance_pct >= 0 ? '+' : ''}{(e.variance_pct || 0).toFixed(1)}%
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-700 font-medium">{(e.excess_litres || 0).toFixed(1)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-red-700 font-medium">
                          {formatINR((e.excess_litres || 0) * (d.avg_rate_per_litre || 95.86))}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${e.status === '⚠ OVER' ? 'bg-red-100 text-red-700' : e.status === 'Check' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {e.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-emerald-200 rounded-sm p-3 bg-emerald-50 flex items-center gap-2 text-sm text-emerald-800" data-testid="no-theft-alerts">
          <span className="text-lg">&#10003;</span>
          <div>
            <strong>No diesel theft alerts.</strong> Tank stocks reconcile correctly and all equipment consumption is within tolerance.
          </div>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.2 / 2.3 Master tables — Vehicle & Equipment (shared pattern)
// ────────────────────────────────────────────────────────────
function MasterTable({ title, icon, fetchUrl, deleteUrl, columns, FormComponent }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const refresh = () => apiGet(fetchUrl).then(d => setRows(d.items || []));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);
  const remove = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try { await apiDel(`${deleteUrl}/${id}`); toast.success('Deleted'); refresh(); } catch { toast.error('Failed'); }
  };
  return (
    <div className="p-6 space-y-4">
      <PageHeader title={title} icon={icon}
        actions={<button onClick={() => setShowNew(true)} className="px-3 py-1.5 bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm text-xs font-display font-bold flex items-center gap-1.5"><Plus size={14} /> Add</button>}
      />
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr>{columns.map(c => <th key={c.k} className={`px-3 py-2 ${c.right ? 'text-right' : 'text-left'}`}>{c.label}</th>)}<th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                {columns.map(c => (
                  <td key={c.k} className={`px-3 py-2 text-xs ${c.right ? 'text-right tabular-nums' : ''}`}>
                    {c.k === 'project_name'
                      ? (r.project_name || r.office_location_name || '—')
                      : (c.fmt ? c.fmt(r[c.k]) : r[c.k] ?? '—')}
                  </td>
                ))}
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing(r)} className="text-[10px] text-[#3b4872] hover:underline mr-2">Edit</button>
                  <button onClick={() => remove(r.id)} className="text-[10px] text-rose-600 hover:underline">Del</button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={columns.length + 1} className="text-center text-slate-400 italic py-6">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {(showNew || editing) && (
        <FormComponent existing={editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={() => { setShowNew(false); setEditing(null); refresh(); }} />
      )}
    </div>
  );
}


export function VehicleMaster() {
  return <MasterTable
    title="Vehicle Master" icon={<Truck size={18} />}
    fetchUrl="/fuel/vehicles" deleteUrl="/fuel/vehicles"
    columns={[
      { k: 'vehicle_number', label: 'Vehicle #' }, { k: 'vehicle_type', label: 'Type' },
      { k: 'ownership', label: 'Ownership' }, { k: 'fuel_type', label: 'Fuel' },
      { k: 'tank_capacity_l', label: 'Tank L', right: true },
      { k: 'mileage_benchmark_kmpl', label: 'Benchmark kmpl', right: true },
      { k: 'driver_name', label: 'Driver' }, { k: 'project_name', label: 'Project / Office' },
      { k: 'status', label: 'Status' },
    ]}
    FormComponent={VehicleForm}
  />;
}

function VehicleForm({ existing, onClose, onSaved }) {
  const [offices, setOffices] = React.useState([]);
  const [drivers, setDrivers] = useState([]);
  const locType = existing?.office_location_id ? 'office' : 'project';
  const [form, setForm] = useState(existing || {
    vehicle_number: '', vehicle_type: 'Truck', ownership: 'Own',
    fuel_type: 'Diesel', tank_capacity_l: 0, mileage_benchmark_kmpl: 0,
    driver_id: '', driver_name: '', project_name: '', project_id: '',
    office_location_id: '', office_location_name: '', status: 'Active',
  });
  const [locationMode, setLocationMode] = useState(locType);

  const loadDrivers = () => {
    apiGet('/fuel/drivers').then(r => setDrivers(r?.items || [])).catch(() => {});
  };

  React.useEffect(() => {
    apiGet('/mobile/attendance/office-locations')
      .then((r) => setOffices(r?.items || r?.offices || []))
      .catch(() => {});
    loadDrivers();
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = {
        ...form,
        // Clear the unused location type before saving
        project_name: locationMode === 'project' ? (form.project_name || '') : '',
        project_id: locationMode === 'project' ? (form.project_id || '') : '',
        office_location_id: locationMode === 'office' ? (form.office_location_id || '') : '',
        office_location_name: locationMode === 'office' ? (form.office_location_name || '') : '',
      };
      if (existing) {
        await apiPut(`/fuel/vehicles/${existing.id}`, payload);
      } else {
        const newVehicle = await apiPost('/fuel/vehicles', payload);
        // assign-driver needs a real vehicle id — must be called after create
        if (form.driver_id && newVehicle?.id) {
          try {
            await apiPut(`/fuel/vehicles/${newVehicle.id}/assign-driver`, { driver_id: form.driver_id });
          } catch (e) {
            toast.error(e?.response?.data?.detail?.message || 'Vehicle saved but driver assignment failed');
          }
        }
      }
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Modal title={existing ? 'Edit Vehicle' : 'New Vehicle'} onClose={onClose} onSave={save}>
      <Field label="Vehicle Number"><input value={form.vehicle_number} onChange={(e) => set('vehicle_number', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Type"><select value={form.vehicle_type} onChange={(e) => set('vehicle_type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">{VEHICLE_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Ownership"><select value={form.ownership} onChange={(e) => set('ownership', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"><option>Own</option><option>Hired</option></select></Field>
      <Field label="Fuel Type"><select value={form.fuel_type} onChange={(e) => set('fuel_type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">{FUEL_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Tank Capacity (L)"><input type="number" value={form.tank_capacity_l} onChange={(e) => set('tank_capacity_l', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Mileage Benchmark (kmpl)"><input type="number" step="0.1" value={form.mileage_benchmark_kmpl} onChange={(e) => set('mileage_benchmark_kmpl', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Driver">
        <select
          value={form.driver_id || ''}
          onChange={async (e) => {
            const newDriverId = e.target.value || null;
            const matchedDriver = drivers.find(d => d.id === newDriverId);
            set('driver_id', newDriverId);
            set('driver_name', matchedDriver?.name || '');
            // For existing vehicles: persist assignment immediately so
            // the 1-driver-1-vehicle rule runs and previous assignment clears
            if (existing?.id) {
              try {
                await apiPut(`/fuel/vehicles/${existing.id}/assign-driver`, { driver_id: newDriverId });
                loadDrivers();
              } catch (err) {
                toast.error(err?.response?.data?.detail?.message || 'Could not assign driver');
              }
            }
          }}
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
          data-testid="fuel-vehicle-driver-select"
        >
          <option value="">— Unassigned —</option>
          {drivers.map(d => (
            <option
              key={d.id}
              value={d.id}
              disabled={!!(d.current_vehicle_id && d.current_vehicle_id !== (existing?.id || form.id))}
            >
              {d.name}{d.current_vehicle_id && d.current_vehicle_id !== (existing?.id || form.id)
                ? ` (assigned to ${d.current_vehicle_number || 'another vehicle'})`
                : ''}
            </option>
          ))}
          {drivers.length === 0 && (
            <option disabled>No drivers yet — add in Driver List</option>
          )}
        </select>
      </Field>
      {/* Location — Project OR Office */}
      <Field label="Assigned To">
        <div className="flex gap-2 mb-2">
          <button type="button" onClick={() => setLocationMode('project')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-sm border transition-colors ${locationMode === 'project' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-[#3b4872]'}`}
            data-testid="fuel-vehicle-loc-project">
            Site / Project
          </button>
          <button type="button" onClick={() => setLocationMode('office')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-sm border transition-colors ${locationMode === 'office' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-[#3b4872]'}`}
            data-testid="fuel-vehicle-loc-office">
            Head Office
          </button>
        </div>
        {locationMode === 'project'
          ? <ProjectStrictPicker value={form.project_name} onChange={(v) => set('project_name', v)} testid="fuel-vehicle-project" />
          : <select value={form.office_location_id || ''} onChange={(e) => {
              const sel = offices.find(o => o.id === e.target.value);
              set('office_location_id', e.target.value);
              set('office_location_name', sel?.name || '');
            }} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="fuel-vehicle-office">
              <option value="">— Select office location —</option>
              {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
        }
      </Field>
      <GpsSection form={form} set={set} existing={existing} />
    </Modal>
  );
}


export function EquipmentMaster() {
  return <MasterTable
    title="Equipment Master" icon={<Wrench size={18} />}
    fetchUrl="/fuel/equipment" deleteUrl="/fuel/equipment"
    columns={[
      { k: 'equipment_id', label: 'Equip ID' }, { k: 'equipment_type', label: 'Type' },
      { k: 'make_model', label: 'Make/Model' }, { k: 'ownership', label: 'Ownership' },
      { k: 'fuel_benchmark_lph', label: 'Benchmark L/hr', right: true },
      { k: 'hour_meter', label: 'Hours', right: true },
      { k: 'operator_name', label: 'Operator' }, { k: 'project_name', label: 'Project' },
      { k: 'status', label: 'Status' },
    ]}
    FormComponent={EquipmentForm}
  />;
}

function EquipmentForm({ existing, onClose, onSaved }) {
  const [offices, setOffices] = React.useState([]);
  const locType = existing?.office_location_id ? 'office' : 'project';
  const [form, setForm] = useState(existing || {
    equipment_id: '', equipment_type: 'Excavator', make_model: '', ownership: 'Own',
    fuel_type: 'Diesel', tank_capacity_l: 500, fuel_benchmark_lph: 0, hour_meter: 0,
    operator_name: '', project_name: '', project_id: '',
    office_location_id: '', office_location_name: '', status: 'Active',
  });
  const [locationMode, setLocationMode] = useState(locType);

  React.useEffect(() => {
    apiGet('/mobile/attendance/office-locations')
      .then((r) => setOffices(r?.items || r?.offices || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = {
        ...form,
        project_name: locationMode === 'project' ? (form.project_name || '') : '',
        project_id: locationMode === 'project' ? (form.project_id || '') : '',
        office_location_id: locationMode === 'office' ? (form.office_location_id || '') : '',
        office_location_name: locationMode === 'office' ? (form.office_location_name || '') : '',
      };
      if (existing) await apiPut(`/fuel/equipment/${existing.id}`, payload);
      else await apiPost('/fuel/equipment', payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Modal title={existing ? 'Edit Equipment' : 'New Equipment'} onClose={onClose} onSave={save}>
      <Field label="Equipment ID"><input value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Type"><select value={form.equipment_type} onChange={(e) => set('equipment_type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">{EQUIPMENT_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Make / Model"><input value={form.make_model || ''} onChange={(e) => set('make_model', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Ownership"><select value={form.ownership} onChange={(e) => set('ownership', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"><option>Own</option><option>Hired</option></select></Field>
      <Field label="Fuel Benchmark (L/hr)"><input type="number" step="0.1" value={form.fuel_benchmark_lph} onChange={(e) => set('fuel_benchmark_lph', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Hour Meter (current)"><input type="number" value={form.hour_meter} onChange={(e) => set('hour_meter', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Operator Name"><input value={form.operator_name || ''} onChange={(e) => set('operator_name', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      {/* Location — Project OR Office */}
      <Field label="Assigned To">
        <div className="flex gap-2 mb-2">
          <button type="button" onClick={() => setLocationMode('project')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-sm border transition-colors ${locationMode === 'project' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-[#3b4872]'}`}
            data-testid="fuel-equip-loc-project">
            Site / Project
          </button>
          <button type="button" onClick={() => setLocationMode('office')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-sm border transition-colors ${locationMode === 'office' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-[#3b4872]'}`}
            data-testid="fuel-equip-loc-office">
            Head Office
          </button>
        </div>
        {locationMode === 'project'
          ? <ProjectStrictPicker value={form.project_name} onChange={(v) => set('project_name', v)} testid="fuel-equipment-project" />
          : <select value={form.office_location_id || ''} onChange={(e) => {
              const sel = offices.find(o => o.id === e.target.value);
              set('office_location_id', e.target.value);
              set('office_location_name', sel?.name || '');
            }} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="fuel-equipment-office">
              <option value="">— Select office location —</option>
              {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
        }
      </Field>
    </Modal>
  );
}


function Modal({ title, onClose, onSave, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={onSave} className="px-4 py-1.5 bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm text-sm font-display font-bold">Save</button>
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.4 Refuelling Log
// ────────────────────────────────────────────────────────────
export function RefuellingLog() {
  const { useAuth } = require('../../contexts/AuthContext');
  const { user } = useAuth?.() || {};
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [logDetail, setLogDetail] = useState({});   // id → full log data
  const [showForm, setShowForm] = useState(false);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [filters, setFilters] = useState({ kind: '', fuel_type: '', date_from: '', date_to: '' });

  const refresh = (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    apiGet('/fuel/logs', params).then(d => setRows(d.items || []));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { apiGet('/fuel/fuel-types').then(d => setFuelTypes(d.values || [])).catch(() => {}); }, []);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const toggleExpand = async (row) => {
    if (expandedId === row.id) { setExpandedId(null); return; }
    setExpandedId(row.id);
    if (!logDetail[row.id]) {
      try {
        const d = await apiGet(`/fuel/logs/${row.id}`);
        setLogDetail(prev => ({ ...prev, [row.id]: d }));
      } catch {}
    }
  };

  const deleteLog = async (id) => {
    if (!window.confirm('Delete this fuel log? This cannot be undone.')) return;
    try {
      await apiDel(`/fuel/logs/${id}`);
      toast.success('Log deleted');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));
      const r = await api.get(`/fuel/logs/export.csv?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'fuel-logs.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const PHOTO_SLOTS = [
    { slot: 'meter_zero', label: 'Meter at 0' },
    { slot: 'vehicle', label: 'Vehicle' },
    { slot: 'meter_final', label: 'Final Meter' },
    { slot: 'pump_bill', label: 'Pump Bill' },
    { slot: 'driver_selfie', label: 'Driver Selfie' },
  ];

  // Change 3 — Lightbox state
  const [lightbox, setLightbox] = useState(null); // { src, label } | null

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="p-6 space-y-4">
      {/* Lightbox overlay */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center"
          onClick={() => setLightbox(null)}
          data-testid="fuel-photo-lightbox"
        >
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
            <X size={28} />
          </button>
          <div className="text-white/60 text-xs uppercase tracking-widest mb-3">{lightbox.label}</div>
          <img
            src={lightbox.src}
            alt={lightbox.label}
            className="max-w-full max-h-[80vh] object-contain rounded-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex gap-3 mt-4" onClick={(e) => e.stopPropagation()}>
            <a href={lightbox.src} download
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-sm text-xs font-display font-bold">
              <Download size={14} /> Download
            </a>
            <a href={lightbox.src} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-sm text-xs font-display font-bold">
              <ExternalLink size={14} /> Open Full Size
            </a>
          </div>
        </div>
      )}
      <PageHeader title="Refuelling Log" icon={<Activity size={18} />}
        actions={(
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} data-testid="fuel-export-csv-btn" className="px-3 py-1.5 border border-slate-300 hover:border-slate-400 rounded-sm text-xs font-display font-bold flex items-center gap-1.5"><Download size={13} /> Export CSV</button>
            <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm text-xs font-display font-bold flex items-center gap-1.5"><Plus size={14} /> New Log</button>
          </div>
        )}
      />
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="fuel-log-filters">
        <select value={filters.kind} onChange={(e) => setF('kind', e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm bg-white">
          <option value="">All kinds</option><option value="vehicle">Vehicle</option><option value="equipment">Equipment</option><option value="tank_refill">Tank Refill</option>
        </select>
        <select value={filters.fuel_type} onChange={(e) => setF('fuel_type', e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm bg-white" data-testid="fuel-filter-type">
          <option value="">All fuel types</option>
          {fuelTypes.map(t => <option key={t}>{t}</option>)}
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} className="px-2 py-1 border border-slate-300 rounded-sm" />
        <span className="text-slate-400">→</span>
        <input type="date" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} className="px-2 py-1 border border-slate-300 rounded-sm" />
        {(filters.kind || filters.fuel_type || filters.date_from || filters.date_to) && (
          <button onClick={() => setFilters({ kind: '', fuel_type: '', date_from: '', date_to: '' })} className="text-[#3b4872] hover:underline">Clear</button>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr>
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Asset</th><th className="px-3 py-2 text-left">Project</th>
              <th className="px-3 py-2 text-right">Litres</th><th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">By</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const detail = logDetail[r.id];
              const photos = detail?.evidence || {};
              const hasPhotos = Object.keys(photos).length > 0;
              const isOpen = expandedId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleExpand(r)}
                    data-testid={`fuel-log-row-${r.id}`}
                  >
                    <td className="px-3 py-2 text-xs text-slate-400">{isOpen ? '▼' : '▶'}</td>
                    <td className="px-3 py-2 text-xs">{_fmtDT(r.log_datetime)}</td>
                    <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-slate-100">{r.kind}</span></td>
                    <td className="px-3 py-2 text-xs">{r.vehicle_name || r.equipment_name}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[180px]">{r.project_name}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">{r.fuel_quantity_l?.toFixed(1)}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">{r.rate_per_l?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums font-bold">{formatINR(r.total_amount)}</td>
                    <td className="px-3 py-2 text-xs">{r.operator_or_driver || r.created_by}</td>
                    <td className="px-3 py-2 text-xs" onClick={e => e.stopPropagation()}>
                      {isSuperAdmin && (
                        <button onClick={() => deleteLog(r.id)} className="text-[10px] text-rose-600 hover:underline" data-testid={`fuel-log-delete-${r.id}`}>Delete</button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-slate-100 bg-slate-50">
                      <td colSpan="10" className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Log details */}
                          <div className="space-y-1 text-xs">
                            <div className="font-bold text-slate-600 mb-2">Log Details</div>
                            {detail ? (
                              <>
                                <div><span className="text-slate-400 w-32 inline-block">Fuel Type:</span> {detail.fuel_type || '—'}</div>
                                <div><span className="text-slate-400 w-32 inline-block">Odometer:</span> {detail.odometer_reading ? `${detail.odometer_reading} km` : '—'}</div>
                                <div><span className="text-slate-400 w-32 inline-block">Hour Meter:</span> {detail.hour_meter_reading ? `${detail.hour_meter_reading} hrs` : '—'}</div>
                                <div><span className="text-slate-400 w-32 inline-block">Fuel Station:</span> {detail.fuel_station || '—'}</div>
                                <div><span className="text-slate-400 w-32 inline-block">Payment:</span> {detail.payment_mode || '—'}</div>
                                {detail.gps_lat && <div><span className="text-slate-400 w-32 inline-block">GPS:</span> {detail.gps_lat?.toFixed(4)}, {detail.gps_lng?.toFixed(4)}</div>}
                                {detail.remarks && <div><span className="text-slate-400 w-32 inline-block">Remarks:</span> {detail.remarks}</div>}
                              </>
                            ) : <div className="text-slate-400">Loading…</div>}
                          </div>
                          {/* Photos */}
                          <div>
                            <div className="font-bold text-slate-600 mb-2 text-xs">Evidence Photos</div>
                            {!detail ? (
                              <div className="text-xs text-slate-400">Loading…</div>
                            ) : !hasPhotos ? (
                              <div className="text-xs text-slate-400 italic">No photos captured for this log.</div>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {PHOTO_SLOTS.map(({ slot, label }) =>
                                  photos[slot] ? (
                                    <div key={slot} className="space-y-1">
                                      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
                                      <div className="relative group cursor-pointer"
                                        onClick={() => setLightbox({
                                          src: `${process.env.REACT_APP_BACKEND_URL}/api/fuel/logs/${r.id}/evidence/${slot}`,
                                          label,
                                        })}
                                      >
                                        <img
                                          src={photos[slot]?.url
                                            ? `${process.env.REACT_APP_BACKEND_URL}${photos[slot].url}`
                                            : `${process.env.REACT_APP_BACKEND_URL}/api/fuel/logs/${r.id}/evidence/${slot}`}
                                          alt={label}
                                          className="w-full rounded-sm border border-slate-200 object-cover transition-opacity group-hover:opacity-80"
                                          style={{ maxHeight: 120 }}
                                          onError={e => { e.target.parentElement.style.display = 'none'; }}
                                          data-testid={`fuel-log-photo-${slot}`}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                          <div className="bg-black/60 text-white rounded-full p-1.5">
                                            <ZoomIn size={16} />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : null
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!rows.length && <tr><td colSpan="10" className="text-center text-slate-400 italic py-6 text-sm">No fuel logs yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <FuelLogForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}
    </div>
  );
}

function FuelLogForm({ onClose, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [form, setForm] = useState({
    kind: 'vehicle', vehicle_id: '', equipment_id: '', tank_id: '',
    log_datetime: new Date().toISOString().slice(0, 16),
    fuel_type: '', fuel_quantity_l: 0, rate_per_l: 94, total_amount: 0, odometer_reading: 0, hour_meter_reading: 0,
    fuel_station: '', payment_mode: 'Fuel Card', remarks: '',
    // Prompt 1
    bill_number: '', bill_date: '', grn_number: '',
    opening_balance_l: '', closing_balance_l: '',
    // Prompt 2
    start_meter_reading: '', start_meter_time: '',
    end_meter_reading: '', end_meter_time: '',
    idle_hours: 0, breakdown_hours: 0,
    // Prompt 3
    work_description: '', work_quantity: '', work_unit: '',
    // Prompt 4
    issue_slip_number: '',
    use_issue_slip: false,
    // Prompt 5
    excess_reason: '', excess_reason_confirmed: false,
  });
  const [validation, setValidation] = useState(null);
  const [excessWarning, setExcessWarning] = useState(null);
  const [dailyTotalWarning, setDailyTotalWarning] = useState(null);
  const [issuingSlip, setIssuingSlip] = useState(false);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'fuel_quantity_l' || k === 'rate_per_l') {
      next.total_amount = Number(next.fuel_quantity_l || 0) * Number(next.rate_per_l || 0);
    }
    return next;
  });

  // Compute running hours client-side for soft validation
  const runningHours = (() => {
    const s = parseFloat(form.start_meter_reading);
    const e = parseFloat(form.end_meter_reading);
    if (!isNaN(s) && !isNaN(e) && e >= s) return e - s;
    return 0;
  })();

  const dailyTotal = runningHours + parseFloat(form.idle_hours || 0) + parseFloat(form.breakdown_hours || 0);

  useEffect(() => {
    apiGet('/fuel/vehicles').then(d => setVehicles(d.items || []));
    apiGet('/fuel/equipment').then(d => setEquipment(d.items || []));
    apiGet('/fuel/tanks').then(d => setTanks(d.items || []));
    apiGet('/fuel/fuel-types').then(d => setFuelTypes(d.values || [])).catch(() => {});
  }, []);

  // Auto-fill opening_balance_l when vehicle/equipment changes
  useEffect(() => {
    if (form.kind === 'vehicle' && form.vehicle_id) {
      apiGet(`/fuel/logs/prev-closing-balance?vehicle_id=${form.vehicle_id}`)
        .then(d => { if (d.closing_balance_l != null) set('opening_balance_l', d.closing_balance_l); })
        .catch(() => {});
    } else if (form.kind === 'equipment' && form.equipment_id) {
      apiGet(`/fuel/logs/prev-closing-balance?equipment_id=${form.equipment_id}`)
        .then(d => { if (d.closing_balance_l != null) set('opening_balance_l', d.closing_balance_l); })
        .catch(() => {});
    }
  }, [form.vehicle_id, form.equipment_id, form.kind]); // eslint-disable-line

  // Auto-fill work_unit from equipment master
  useEffect(() => {
    if (form.kind === 'equipment' && form.equipment_id) {
      const eq = equipment.find(e => e.id === form.equipment_id);
      if (eq?.output_unit && !form.work_unit) set('work_unit', eq.output_unit);
    }
  }, [form.equipment_id]); // eslint-disable-line

  const totalFuelAvailable = (() => {
    const ob = parseFloat(form.opening_balance_l);
    const qty = parseFloat(form.fuel_quantity_l || 0);
    if (!isNaN(ob)) return (ob + qty).toFixed(1);
    return null;
  })();

  const validate = async () => {
    try {
      const d = await apiPost('/fuel/validate-log', { ...form, log_datetime: new Date(form.log_datetime).toISOString() });
      setValidation(d);
    } catch (e) { toast.error('Validate failed'); }
  };

  const createIssueSlip = async () => {
    if (!form.tank_id) return toast.error('Select a tank first');
    if (!form.fuel_quantity_l) return toast.error('Enter fuel quantity first');
    const assetId = form.kind === 'vehicle' ? form.vehicle_id : form.equipment_id;
    if (!assetId) return toast.error('Select a vehicle/equipment first');
    setIssuingSlip(true);
    try {
      const slip = await apiPost('/fuel/issue-slips', {
        tank_id: form.tank_id,
        issued_to_kind: form.kind,
        issued_to_id: assetId,
        quantity_l: parseFloat(form.fuel_quantity_l),
      });
      set('issue_slip_number', slip.slip_number);
      toast.success(`Issue Slip ${slip.slip_number} created. Tank deducted by ${form.fuel_quantity_l}L.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create issue slip');
    } finally { setIssuingSlip(false); }
  };

  const save = async (rateConfirmed = false, excessConfirmed = false) => {
    try {
      const payload = {
        ...form,
        rate_confirmed: rateConfirmed,
        excess_reason_confirmed: excessConfirmed,
        log_datetime: new Date(form.log_datetime).toISOString(),
        opening_balance_l: form.opening_balance_l !== '' ? parseFloat(form.opening_balance_l) : null,
        closing_balance_l: form.closing_balance_l !== '' ? parseFloat(form.closing_balance_l) : null,
        start_meter_reading: form.start_meter_reading !== '' ? parseFloat(form.start_meter_reading) : null,
        end_meter_reading: form.end_meter_reading !== '' ? parseFloat(form.end_meter_reading) : null,
        start_meter_time: form.start_meter_time ? new Date(form.start_meter_time).toISOString() : null,
        end_meter_time: form.end_meter_time ? new Date(form.end_meter_time).toISOString() : null,
        work_quantity: form.work_quantity !== '' ? parseFloat(form.work_quantity) : null,
        idle_hours: parseFloat(form.idle_hours || 0),
        breakdown_hours: parseFloat(form.breakdown_hours || 0),
        // If using issue slip, do NOT deduct tank again (issue slip already did it)
        tank_id: form.issue_slip_number ? null : (form.tank_id || null),
      };
      const res = await apiPost('/fuel/logs', payload);
      if (res?.rate_warning) {
        if (window.confirm(res.rate_warning)) return save(true, excessConfirmed);
        return;
      }
      if (res?.excess_warning) {
        setExcessWarning({ msg: res.excess_warning, variance_pct: res.variance_pct });
        return;
      }
      toast.success('Log saved');
      if (res?.daily_total_warning) toast.warning(res.daily_total_warning, { duration: 6000 });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Save failed');
    }
  };

  const selectedEquipment = form.kind === 'equipment' ? equipment.find(e => e.id === form.equipment_id) : null;
  const benchmarkLph = selectedEquipment?.fuel_benchmark_lph || 0;
  const computedVariancePct = (() => {
    if (runningHours > 0 && form.fuel_quantity_l > 0 && benchmarkLph > 0) {
      const acr = form.fuel_quantity_l / runningHours;
      return ((acr - benchmarkLph) / benchmarkLph) * 100;
    }
    return null;
  })();

  const inp = 'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm';
  const inpRO = 'w-full px-3 py-2 border border-slate-200 bg-slate-50 rounded-sm text-sm';

  return (
    <Modal title="New Refuelling Log" onClose={onClose} onSave={() => save(false, false)}>
      {/* Excess reason warning popup */}
      {excessWarning && (
        <div className="md:col-span-2 bg-rose-50 border border-rose-200 rounded p-3 space-y-2">
          <p className="text-sm font-bold text-rose-700">{excessWarning.msg}</p>
          <Field label="Reason for Excess Consumption *">
            <textarea rows={2} value={form.excess_reason} onChange={e => set('excess_reason', e.target.value)} className={inp} placeholder="Explain why consumption exceeded benchmark…" data-testid="excess-reason-input" />
          </Field>
          <div className="flex gap-2">
            <button onClick={() => { if (!form.excess_reason.trim()) return toast.error('Reason is required'); setExcessWarning(null); save(false, true); }}
              className="px-3 py-1.5 text-xs bg-rose-600 text-white rounded-sm font-bold" data-testid="excess-reason-confirm">
              Confirm & Save
            </button>
            <button onClick={() => setExcessWarning(null)} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm">Cancel</button>
          </div>
        </div>
      )}

      <Field label="Kind">
        <select value={form.kind} onChange={(e) => set('kind', e.target.value)} className={inp}>
          <option value="vehicle">Vehicle</option><option value="equipment">Equipment</option><option value="tank_refill">Tank Refill</option>
        </select>
      </Field>
      {form.kind === 'vehicle' ? (
        <Field label="Vehicle"><select value={form.vehicle_id} onChange={(e) => set('vehicle_id', e.target.value)} className={inp}><option value="">Select…</option>{vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}</select></Field>
      ) : form.kind === 'equipment' ? (
        <Field label="Equipment"><select value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} className={inp}><option value="">Select…</option>{equipment.map(e => <option key={e.id} value={e.id}>{e.equipment_id}</option>)}</select></Field>
      ) : <Field label=""><div className="text-xs text-slate-500 px-3 py-2">Tank refill — assign tank in the next field</div></Field>}

      <Field label="Tank (source / refill target)">
        <select value={form.tank_id} onChange={(e) => set('tank_id', e.target.value)} className={inp} data-testid="fuel-form-tank">
          <option value="">— None / Direct Pump —</option>
          {tanks.map(t => <option key={t.id} value={t.id}>{`${t.tank_id} · ${Math.round(t.current_level_l || 0)}L`}</option>)}
        </select>
      </Field>

      {/* Prompt 4 — Issue Slip flow */}
      {form.kind !== 'tank_refill' && form.tank_id && (
        <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-sm p-3 space-y-2" data-testid="issue-slip-section">
          <div className="text-xs font-bold text-blue-700">Issue Fuel from Tank</div>
          {form.issue_slip_number ? (
            <div className="text-xs text-emerald-700 font-bold">Slip {form.issue_slip_number} created — tank already deducted</div>
          ) : (
            <button onClick={createIssueSlip} disabled={issuingSlip}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold disabled:opacity-50 flex items-center gap-1.5"
              data-testid="create-issue-slip-btn">
              {issuingSlip ? <Loader2 size={12} className="animate-spin" /> : null}
              Create Issue Slip (FIS) & Deduct Tank
            </button>
          )}
          {form.issue_slip_number && (
            <Field label="Issue Slip No.">
              <input value={form.issue_slip_number} readOnly className={inpRO} data-testid="issue-slip-number" />
            </Field>
          )}
        </div>
      )}

      <Field label="Fuel Type">
        <select value={form.fuel_type} onChange={(e) => set('fuel_type', e.target.value)} className={inp} data-testid="fuel-form-type">
          <option value="">— Select —</option>
          {fuelTypes.map(t => <option key={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Date / Time"><input type="datetime-local" value={form.log_datetime} onChange={(e) => set('log_datetime', e.target.value)} className={inp} /></Field>
      <Field label="Fuel Quantity (L)"><input type="number" step="0.1" value={form.fuel_quantity_l} onChange={(e) => set('fuel_quantity_l', Number(e.target.value))} className={inp} /></Field>
      <Field label="Rate per L (₹)"><input type="number" step="0.01" value={form.rate_per_l} onChange={(e) => set('rate_per_l', Number(e.target.value))} className={inp} /></Field>
      <Field label="Total (₹)"><input type="number" value={form.total_amount} readOnly className={inpRO} /></Field>

      {/* Prompt 1 — Bill/GRN */}
      <Field label="Bill No."><input value={form.bill_number} onChange={e => set('bill_number', e.target.value)} className={inp} placeholder="e.g. BILL-001" data-testid="fuel-form-bill-no" /></Field>
      <Field label="Bill Date"><input type="date" value={form.bill_date} onChange={e => set('bill_date', e.target.value)} className={inp} data-testid="fuel-form-bill-date" /></Field>
      <Field label="GRN No."><input value={form.grn_number} onChange={e => set('grn_number', e.target.value)} className={inp} placeholder="GRN if from store" data-testid="fuel-form-grn" /></Field>

      {/* Prompt 1 — In-machine fuel balance */}
      {form.kind !== 'tank_refill' && (
        <>
          <Field label="Opening Balance (L)">
            <input type="number" step="0.1" value={form.opening_balance_l} onChange={e => set('opening_balance_l', e.target.value)} className={inp} placeholder="Auto-filled from prev. log" data-testid="fuel-form-opening-balance" />
          </Field>
          <Field label="Closing Balance (L)">
            <input type="number" step="0.1" value={form.closing_balance_l} onChange={e => set('closing_balance_l', e.target.value)} className={inp} placeholder="Fuel remaining in machine" data-testid="fuel-form-closing-balance" />
          </Field>
          {totalFuelAvailable && (
            <div className="text-xs text-slate-600 px-1 mt-1 font-medium">
              Total fuel available = {totalFuelAvailable} L (opening + filled)
            </div>
          )}
        </>
      )}

      {/* Prompt 2 — Shift start/end meter readings */}
      {form.kind !== 'tank_refill' && (
        <div className="md:col-span-2 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-display text-slate-500">Shift Meter Readings</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starting Meter Reading">
              <input type="number" step="0.01" value={form.start_meter_reading} onChange={e => set('start_meter_reading', e.target.value)} className={inp} placeholder="e.g. 2413" data-testid="fuel-form-start-meter" />
            </Field>
            <Field label="Start Time">
              <input type="datetime-local" value={form.start_meter_time} onChange={e => set('start_meter_time', e.target.value)} className={inp} data-testid="fuel-form-start-meter-time" />
            </Field>
            <Field label="Closing Meter Reading">
              <input type="number" step="0.01" value={form.end_meter_reading} onChange={e => set('end_meter_reading', e.target.value)} className={inp} placeholder="e.g. 2430" data-testid="fuel-form-end-meter" />
            </Field>
            <Field label="End Time">
              <input type="datetime-local" value={form.end_meter_time} onChange={e => set('end_meter_time', e.target.value)} className={inp} data-testid="fuel-form-end-meter-time" />
            </Field>
          </div>
          {runningHours > 0 && (
            <div className="text-xs text-emerald-700 font-medium">
              Running hours: {runningHours.toFixed(2)} hrs
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Idle Time (hrs)">
              <input type="number" step="0.1" min="0" value={form.idle_hours} onChange={e => set('idle_hours', e.target.value)} className={inp} data-testid="fuel-form-idle-hours" />
            </Field>
            <Field label="Breakdown Time (hrs)">
              <input type="number" step="0.1" min="0" value={form.breakdown_hours} onChange={e => set('breakdown_hours', e.target.value)} className={inp} data-testid="fuel-form-breakdown-hours" />
            </Field>
          </div>
          {dailyTotal > 24 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="daily-total-warning">
              Warning: total hours ({dailyTotal.toFixed(1)}h) exceeds 24h for this shift
            </div>
          )}
        </div>
      )}

      {/* Prompt 3 — Work done (equipment only) */}
      {form.kind === 'equipment' && (
        <div className="md:col-span-2 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-display text-slate-500">Nature & Quantity of Work Done</div>
          <Field label="Work Description">
            <input value={form.work_description} onChange={e => set('work_description', e.target.value)} className={inp} placeholder="e.g. Excavation, Rock breaking" data-testid="fuel-form-work-desc" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity">
              <input type="number" step="0.01" value={form.work_quantity} onChange={e => set('work_quantity', e.target.value)} className={inp} placeholder="e.g. 15" data-testid="fuel-form-work-qty" />
            </Field>
            <Field label="Unit">
              <input value={form.work_unit} onChange={e => set('work_unit', e.target.value)} className={inp} placeholder="e.g. cum, MT" data-testid="fuel-form-work-unit" />
            </Field>
          </div>
        </div>
      )}

      {/* Prompt 5 — Variance display */}
      {computedVariancePct !== null && (
        <div className={`md:col-span-2 text-xs px-2 py-1.5 rounded border font-medium ${Math.abs(computedVariancePct) > 15 ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`} data-testid="variance-display">
          Consumption variance: {computedVariancePct >= 0 ? '+' : ''}{computedVariancePct.toFixed(1)}% vs benchmark {benchmarkLph} L/hr
          {Math.abs(computedVariancePct) > 15 && ' — reason required before save'}
        </div>
      )}

      {form.kind === 'vehicle' && <Field label="Odometer (km)"><input type="number" value={form.odometer_reading} onChange={(e) => set('odometer_reading', Number(e.target.value))} className={inp} /></Field>}
      {form.kind === 'equipment' && <Field label="Hour Meter (end)"><input type="number" value={form.hour_meter_reading} onChange={(e) => set('hour_meter_reading', Number(e.target.value))} className={inp} /></Field>}
      <Field label="Station"><input value={form.fuel_station} onChange={(e) => set('fuel_station', e.target.value)} className={inp} /></Field>
      <Field label="Payment Mode"><select value={form.payment_mode} onChange={(e) => set('payment_mode', e.target.value)} className={inp}><option>Fuel Card</option><option>Cash</option><option>UPI</option><option>Credit Account</option></select></Field>
      <div className="md:col-span-2">
        <button onClick={validate} className="px-3 py-1.5 text-xs bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-sm flex items-center gap-1.5">
          <Gauge size={12} /> Pre-validate against benchmarks
        </button>
        {validation && (
          <ul className="mt-3 space-y-1 text-xs" data-testid="fuel-log-validation">
            {validation.validation.map((c, i) => (
              <li key={i} className="flex items-start gap-2 border-b border-slate-100 pb-1">
                <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase ${c.status === 'pass' ? 'bg-emerald-100 text-emerald-700' : c.status === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{c.status}</span>
                <span><strong>{c.name}</strong> — {c.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}


// ────────────────────────────────────────────────────────────
// 2.5 Tank & Storage
// ────────────────────────────────────────────────────────────
const EMPTY_TANK = { tank_id: '', project_id: '', project_name: '', fuel_type: 'Diesel', capacity_l: '', current_level_l: 0, location: '', custodian: '', sensor_linked: false };

export function TankStorage() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // tank row being edited
  const [form, setForm] = useState(EMPTY_TANK);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => apiGet('/fuel/tanks').then(d => setRows(d.items || []));
  useEffect(() => {
    load();
    apiGet('/fuel/fuel-types').then(d => setFuelTypes(d.values || [])).catch(() => {});
  }, []);

  const openAdd = () => { setForm(EMPTY_TANK); setEditing(null); setShowForm(true); };
  const openEdit = (t) => { setForm({ ...t, capacity_l: t.capacity_l ?? '', current_level_l: t.current_level_l ?? 0 }); setEditing(t); setShowForm(true); };

  const save = async () => {
    if (!form.tank_id.trim()) return toast.error('Tank ID is required');
    if (!form.project_id) return toast.error('Project is required');
    if (!form.capacity_l || Number(form.capacity_l) <= 0) return toast.error('Capacity must be > 0');
    setSaving(true);
    try {
      const payload = { ...form, capacity_l: Number(form.capacity_l), current_level_l: Number(form.current_level_l || 0) };
      if (editing) {
        await apiPut(`/fuel/tanks/${editing.id}`, payload);
        toast.success('Tank updated');
      } else {
        await apiPost('/fuel/tanks', payload);
        toast.success('Tank added');
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Tank & Storage"
        subtitle="On-site fuel tanks — capacity, level, custodian"
        icon={<Droplet size={18} />}
        actions={
          <button onClick={openAdd} className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider" data-testid="add-tank-btn">
            <Plus size={14} /> Add Tank
          </button>
        }
      />

      {/* Add / Edit Tank form */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="tank-form">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display font-bold text-sm">{editing ? 'Edit Tank' : 'Add New Tank'}</h3>
            <button onClick={() => setShowForm(false)}><X size={16} className="text-slate-400" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Tank ID *">
              <input value={form.tank_id} onChange={e => setForm(f => ({ ...f, tank_id: e.target.value }))} placeholder="e.g. TANK-001" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tank-id-input" />
            </Field>
            <Field label="Fuel Type *">
              <select value={form.fuel_type} onChange={e => setForm(f => ({ ...f, fuel_type: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tank-fuel-type">
                {(fuelTypes.length ? fuelTypes : ['Diesel', 'Petrol', 'HSD', 'CNG', 'LPG']).map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Project *">
                <ProjectStrictPicker
                  value={form.project_name || ''}
                  onChange={(name) => setForm(f => ({ ...f, project_name: name || '' }))}
                  onPick={(p) => setForm(f => ({ ...f, project_id: p?.id || '', project_name: p?.name || f.project_name }))}
                  placeholder="Select project…"
                  testid="tank-project-picker"
                />
              </Field>
            </div>
            <Field label="Capacity (L) *">
              <input type="number" min="1" value={form.capacity_l} onChange={e => setForm(f => ({ ...f, capacity_l: e.target.value }))} placeholder="e.g. 5000" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tank-capacity" />
            </Field>
            <Field label="Opening Stock (L)">
              <input type="number" min="0" value={form.current_level_l} onChange={e => setForm(f => ({ ...f, current_level_l: e.target.value }))} placeholder="0" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tank-opening-stock" />
            </Field>
            <Field label="Location / Area">
              <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Site Gate, Yard" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </Field>
            <Field label="Custodian">
              <input value={form.custodian} onChange={e => setForm(f => ({ ...f, custodian: e.target.value }))} placeholder="Person responsible" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </Field>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.sensor_linked} onChange={e => setForm(f => ({ ...f, sensor_linked: e.target.checked }))} className="rounded" />
              IoT / Sensor linked
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-bold bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm disabled:opacity-50 flex items-center gap-1.5" data-testid="tank-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {editing ? 'Update Tank' : 'Add Tank'}
            </button>
          </div>
        </div>
      )}

      {/* Tank cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(t => {
          const pct = t.capacity_l ? Math.min(100, Math.round((t.current_level_l / t.capacity_l) * 100)) : 0;
          const color = pct < 25 ? 'bg-rose-500' : pct < 50 ? 'bg-amber-500' : 'bg-emerald-500';
          return (
            <div key={t.id} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`tank-card-${t.id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-bold">{t.tank_id}</h3>
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-slate-100">{t.fuel_type}</span>
                  <button onClick={() => openEdit(t)} className="text-slate-400 hover:text-[#3b4872] p-0.5" title="Edit tank" data-testid={`tank-edit-${t.id}`}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                </div>
              </div>
              <div className="text-xs text-slate-500">{t.project_name} · {t.location || '—'}</div>
              <div className="h-4 bg-slate-100 rounded mt-3"><div className={`h-4 rounded ${color}`} style={{ width: `${pct}%` }} /></div>
              <div className="flex justify-between text-xs mt-1"><span><strong>{(t.current_level_l || 0).toFixed(0)} L</strong> ({pct}%)</span><span className="text-slate-400">/ {(t.capacity_l || 0).toFixed(0)} L</span></div>
              <div className="text-[10px] text-slate-400 mt-2">Custodian: {t.custodian || '—'} · Sensor: {t.sensor_linked ? 'Yes' : 'No'}</div>
            </div>
          );
        })}
        {!rows.length && !showForm && (
          <div className="text-center text-slate-400 italic py-8 text-sm md:col-span-2">
            No tanks registered yet. <button onClick={openAdd} className="text-[#3b4872] font-bold underline">Add the first tank →</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.6 Fuel Alerts
// ────────────────────────────────────────────────────────────
const ALERT_TYPE_LABELS = {
  bill_authenticity: 'Bill Authenticity Doubtful',
  rate_anomaly: 'Unusual Fuel Rate',
  odometer_regression: 'Odometer Reading Went Down',
  quantity_anomaly: 'Unusual Quantity',
  night_movement: 'Night Movement Detected',
  geofence_breach: 'Refuelling Outside Authorised Area',
  duplicate_log: 'Possible Duplicate Entry',
};

const PHOTO_SLOTS_ALERT = [
  { slot: 'meter_zero', label: 'Meter at Zero' },
  { slot: 'vehicle', label: 'Vehicle' },
  { slot: 'meter_final', label: 'Final Meter' },
  { slot: 'pump_bill', label: 'Pump Bill' },
];

export function FuelAlerts() {
  const [rows, setRows] = useState([]);
  const [logDetails, setLogDetails] = useState({});  // log_id → full log
  const [lightbox, setLightbox] = useState(null); // Change 3C

  const refresh = () => apiGet('/fuel/alerts').then(d => setRows(d.items || []));
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Load fuel log evidence for each alert
  useEffect(() => {
    rows.forEach(a => {
      if (a.log_id && !logDetails[a.log_id]) {
        apiGet(`/fuel/logs/${a.log_id}`).then(d => {
          setLogDetails(prev => ({ ...prev, [a.log_id]: d }));
        }).catch(() => {});
      }
    });
  }, [rows]);  // eslint-disable-line

  const update = async (id, status) => {
    try { await apiPut(`/fuel/alerts/${id}`, { status }); toast.success('Updated'); refresh(); }
    catch { toast.error('Failed'); }
  };

  const addNote = async (id) => {
    const note = window.prompt('Investigator note:');
    if (!note) return;
    try { await apiPut(`/fuel/alerts/${id}`, { investigator_note: note }); toast.success('Note saved'); refresh(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Lightbox for alerts */}
      {lightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center" onClick={() => setLightbox(null)} data-testid="fuel-alert-lightbox">
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X size={28} /></button>
          <div className="text-white/60 text-xs uppercase tracking-widest mb-3">{lightbox.label}</div>
          <img src={lightbox.src} alt={lightbox.label} className="max-w-full max-h-[80vh] object-contain rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <div className="flex gap-3 mt-4" onClick={(e) => e.stopPropagation()}>
            <a href={lightbox.src} download className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-sm text-xs font-display font-bold"><Download size={14} /> Download</a>
            <a href={lightbox.src} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-sm text-xs font-display font-bold"><ExternalLink size={14} /> Open Full Size</a>
          </div>
        </div>
      )}
      <PageHeader title="Fuel Fraud Alerts"
        subtitle="AI-flagged anomalies — review each entry and resolve"
        icon={<AlertTriangle size={18} />} />
      <div className="space-y-4" data-testid="fuel-alerts-list">
        {rows.map(a => {
          const log = logDetails[a.log_id];
          const evidence = log?.evidence || {};
          const hasPhotos = Object.keys(evidence).length > 0;
          const isOpen = a.status !== 'Resolved';
          return (
            <div key={a.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`fuel-alert-${a.id}`}>
              {/* Header */}
              <div className="flex items-start gap-3 px-4 py-3 border-b border-slate-100">
                <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase ${a.severity === 'high' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                  {a.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm">
                    {ALERT_TYPE_LABELS[a.type] || (a.type || '').replaceAll('_', ' ')}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{a.explanation}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap gap-2">
                    <span>{_fmtDT(a.created_at)}</span>
                    <span>AI confidence: {a.ai_confidence}%</span>
                    {log && <span>Vehicle: {log.vehicle_name || log.vehicle_id?.slice(0,8)}</span>}
                    {log && <span>{log.fuel_quantity_l?.toFixed(1)} L · ₹{log.total_amount?.toLocaleString('en-IN')}</span>}
                  </div>
                  {a.investigator_note && (
                    <div className="mt-1.5 text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-800">
                      Note: {a.investigator_note}
                    </div>
                  )}
                </div>
                <span className={`shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${
                  a.status === 'Resolved' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-amber-100 text-amber-700'}`}>
                  {a.status}
                </span>
              </div>

              {/* Photos from the linked fuel log */}
              <div className="px-4 py-3">
                {!log ? (
                  <div className="text-xs text-slate-400">Loading log details…</div>
                ) : !hasPhotos ? (
                  <div className="text-xs text-slate-400 italic">No evidence photos were captured for this fuel log.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    {PHOTO_SLOTS_ALERT.map(({ slot, label }) =>
                      evidence[slot] ? (
                        <div key={slot}>
                          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/fuel/logs/${a.log_id}/evidence/${slot}`}
                            alt={label}
                            className="w-full rounded-sm border border-slate-200 object-cover cursor-pointer hover:opacity-90"
                            style={{ maxHeight: 100 }}
                            onClick={() => setLightbox({ src: `${process.env.REACT_APP_BACKEND_URL}/api/fuel/logs/${a.log_id}/evidence/${slot}`, label: slot })}
                            onError={e => { e.target.parentElement.style.display='none'; }}
                            data-testid={`alert-photo-${slot}`}
                          />
                          {evidence[slot].checked === false && (
                            <div className="text-[9px] text-rose-600 mt-0.5 font-bold">⚠ Unchecked</div>
                          )}
                        </div>
                      ) : null
                    )}
                  </div>
                )}

                {/* Actions — single Resolve button, matching unified FraudAlerts page */}
                {isOpen && (
                  <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
                    <button onClick={() => addNote(a.id)}
                      className="text-[11px] px-2.5 py-1.5 border border-slate-300 rounded hover:bg-slate-50">
                      Add Note
                    </button>
                    <button onClick={() => update(a.id, 'Resolved')}
                      className="text-[11px] px-2.5 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 font-bold"
                      data-testid="fuel-fa-resolve">
                      Resolve
                    </button>
                  </div>
                )}
                {!isOpen && (
                  <div className="pt-2 border-t border-slate-100 text-xs text-slate-400">
                    ✓ Resolved.{a.updated_by && ` By ${a.updated_by}.`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {!rows.length && <div className="text-center text-slate-400 italic py-8 text-sm">No fuel alerts. All clear.</div>}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.7 Driver / Operator Scores
// ────────────────────────────────────────────────────────────
export function DriverScores() {
  const [rows, setRows] = useState([]);
  useEffect(() => { apiGet('/fuel/driver-scores').then(d => setRows(d.items || [])); }, []);
  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Driver / Operator Scores" subtitle="Last 30 days · efficiency & violations" icon={<UsersRound size={18} />} />
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-right">Logs</th>
              <th className="px-3 py-2 text-right">Total L</th><th className="px-3 py-2 text-right">Total ₹</th>
              <th className="px-3 py-2 text-right">Alerts</th><th className="px-3 py-2 text-center">Grade</th>
              <th className="px-3 py-2 text-right">Score</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.name} className="border-t border-slate-100">
                <td className="px-3 py-2 font-display font-semibold">{r.name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.logs}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.total_litres?.toFixed(0)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatINR(r.total_amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.alerts_triggered}</td>
                <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold ${r.grade === 'A' ? 'bg-emerald-100 text-emerald-700' : r.grade === 'B' ? 'bg-lime-100 text-lime-700' : r.grade === 'C' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{r.grade}</span></td>
                <td className="px-3 py-2 text-right font-bold tabular-nums">{r.score}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="7" className="text-center text-slate-400 italic py-6 text-sm">No scores yet — operate the equipment first.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.8 Reports — Phase 3C (PDF + Excel exports)
// ────────────────────────────────────────────────────────────
export function FuelDriverList() {
  const [drivers, setDrivers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [licenseForm, setLicenseForm] = useState({ license_number: '', license_expiry: '', license_class: 'LMV', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = () => apiGet('/fuel/drivers', { active_only: false }).then(d => setDrivers(d.items || []));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!empSearch.trim()) { setEmpResults([]); return; }
    apiGet('/fuel/drivers/eligible-employees', { search: empSearch }).then(r => setEmpResults(r.items || []));
  }, [empSearch]);

  const addDriver = async () => {
    if (!selectedEmp) return toast.error('Select an employee first');
    setSaving(true);
    try {
      await apiPost('/fuel/drivers', { employee_id: selectedEmp.id, ...licenseForm });
      toast.success(`${selectedEmp.name} added to Driver List`);
      setShowAdd(false); setSelectedEmp(null); setEmpSearch(''); setEmpResults([]);
      setLicenseForm({ license_number: '', license_expiry: '', license_class: 'LMV', notes: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    try {
      await apiPut(`/fuel/drivers/${d.id}`, { ...d, is_active: !d.is_active, employee_id: d.employee_id });
      load();
    } catch { toast.error('Failed'); }
  };

  const removeDriver = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the Driver List?`)) return;
    try { await apiDel(`/fuel/drivers/${id}`); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Driver List" subtitle="All authorised drivers — any driver can fuel any vehicle"
        icon={<UsersRound size={18} />}
        actions={<button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold"><Plus size={14} /> Add Driver</button>}
      />

      {showAdd && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="driver-add-form">
          <div className="font-display font-bold text-sm">Add Employee as Driver</div>
          <div className="relative">
            <input value={empSearch} onChange={e => setEmpSearch(e.target.value)}
              placeholder="Search employee by name…"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            {empResults.length > 0 && (
              <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto mt-0.5">
                {empResults.map(e => (
                  <button key={e.id} type="button" onMouseDown={() => { setSelectedEmp(e); setEmpSearch(e.name); setEmpResults([]); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 border-b border-slate-50">
                    <div>
                      <span className="font-medium">{e.name}</span>
                      {e.designation && <span className="text-[10px] ml-1.5 text-slate-400">{e.designation}</span>}
                      {(e.designation || '').toLowerCase().includes('driver') && (
                        <span className="text-[9px] ml-1.5 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">DRIVER</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedEmp && (
            <div className="text-xs bg-slate-50 rounded-sm px-3 py-2 text-slate-700">
              Selected: <strong>{selectedEmp.name}</strong> · {selectedEmp.designation} · {selectedEmp.emp_id}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div><label className="text-slate-500 font-medium">License No.</label>
              <input value={licenseForm.license_number} onChange={e => setLicenseForm(f => ({...f, license_number: e.target.value}))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" /></div>
            <div><label className="text-slate-500 font-medium">Expiry</label>
              <input type="date" value={licenseForm.license_expiry} onChange={e => setLicenseForm(f => ({...f, license_expiry: e.target.value}))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" /></div>
            <div><label className="text-slate-500 font-medium">Class</label>
              <select value={licenseForm.license_class} onChange={e => setLicenseForm(f => ({...f, license_class: e.target.value}))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded-sm text-sm bg-white">
                {['LMV','HMV','Transport','MCWG','LMV-TR'].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div><label className="text-slate-500 font-medium">Notes</label>
              <input value={licenseForm.notes} onChange={e => setLicenseForm(f => ({...f, notes: e.target.value}))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setShowAdd(false); setSelectedEmp(null); setEmpSearch(''); }}
              className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm">Cancel</button>
            <button onClick={addDriver} disabled={saving || !selectedEmp}
              className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm disabled:opacity-40 font-bold" data-testid="driver-add-save">
              {saving ? 'Saving…' : 'Add Driver'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Emp ID</th>
              <th className="px-3 py-2 text-left">License</th><th className="px-3 py-2 text-left">Expiry</th>
              <th className="px-3 py-2 text-left">Assigned Vehicle</th><th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {drivers.map(d => (
              <tr key={d.id} className={`border-t border-slate-100 ${d.is_active ? '' : 'opacity-50'}`} data-testid={`driver-row-${d.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{d.name}</div>
                  {(d.designation || '').toLowerCase().includes('driver') && (
                    <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">DRIVER</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs font-mono text-slate-500">{d.emp_id}</td>
                <td className="px-3 py-2 text-xs">{d.license_number || '—'}{d.license_class && <span className="ml-1 text-[9px] text-slate-400">{d.license_class}</span>}</td>
                <td className="px-3 py-2 text-xs">{d.license_expiry || '—'}</td>
                <td className="px-3 py-2 text-xs">{d.current_vehicle_number || <span className="text-slate-400">Unassigned</span>}</td>
                <td className="px-3 py-2 text-xs">
                  <button onClick={() => toggleActive(d)} className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${d.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {d.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => removeDriver(d.id, d.name)} className="text-[10px] text-rose-600 hover:underline" data-testid={`driver-remove-${d.id}`}>Remove</button>
                </td>
              </tr>
            ))}
            {!drivers.length && <tr><td colSpan="7" className="text-center text-slate-400 italic py-6 text-sm">No drivers registered yet. Add employees from the button above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FuelReports() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    apiGet('/fuel/reports').then(d => setItems(d.items || [])).catch(() => {});
    apiGet('/projects').then(d => setProjects(d.items || d || [])).catch(() => {});
  }, []);

  const download = async (id, fmt) => {
    const key = `${id}-${fmt}`;
    setBusy(key);
    try {
      const resp = await api.get(`/fuel/reports/${id}/export`, {
        params: { format: fmt, month, ...(projectId ? { project_id: projectId } : {}) },
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${id}_${month}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  const downloadDailyRegister = async () => {
    const key = 'daily-register-xlsx';
    setBusy(key);
    try {
      const params = { month };
      if (projectId) params.project_id = projectId;
      const resp = await api.get('/fuel/reports/daily-register/export', {
        params,
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fuel_Daily_Register_${month}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Fuel Reports" subtitle="Exportable PDF / Excel" icon={<PieChart size={18} />} />
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <label className="text-[10px] uppercase tracking-widest font-display text-slate-500">Period</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm"
          data-testid="fuel-reports-month"
        />
        <label className="text-[10px] uppercase tracking-widest font-display text-slate-500">Project</label>
        <select
          value={projectId}
          onChange={e => setProjectId(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm"
          data-testid="fuel-reports-project"
        >
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="text-xs text-slate-500 ml-auto">{items.length + 1} reports available</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fuel-reports-grid">
        {/* Daily Register — special card */}
        <div className="bg-white border border-[#3b4872] rounded-sm p-4 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={18} className="text-[#3b4872]" />
            <span className="text-[9px] uppercase tracking-widest font-bold text-[#3b4872] bg-blue-50 px-1.5 py-0.5 rounded">New</span>
          </div>
          <div className="font-display font-bold text-sm">Daily Register (Excel)</div>
          <div className="text-[10px] text-slate-400 mt-1 flex-1">Exact-match "RECORD OF DIESEL PURCHASED AND CONSUMED" template — all 31 columns</div>
          <div className="mt-3">
            <button
              onClick={downloadDailyRegister}
              disabled={busy === 'daily-register-xlsx'}
              className="w-full px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
              data-testid="fuel-report-daily-register-xlsx"
            >
              {busy === 'daily-register-xlsx' ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Excel
            </button>
          </div>
        </div>

        {items.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col">
            <FileText size={18} className="text-[#3b4872] mb-2" />
            <div className="font-display font-bold text-sm">{r.title}</div>
            <div className="text-[10px] text-slate-400 mt-1 flex-1">Period: {month}</div>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => download(r.id, 'pdf')}
                disabled={busy === `${r.id}-pdf`}
                className="flex-1 px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid={`fuel-report-${r.id}-pdf`}
              >
                {busy === `${r.id}-pdf` ? <Loader2 size={11} className="animate-spin" /> : null} PDF
              </button>
              <button
                onClick={() => download(r.id, 'xlsx')}
                disabled={busy === `${r.id}-xlsx`}
                className="flex-1 px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider border border-[#3b4872] text-[#3b4872] hover:bg-[#3b4872] hover:text-white rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid={`fuel-report-${r.id}-xlsx`}
              >
                {busy === `${r.id}-xlsx` ? <Loader2 size={11} className="animate-spin" /> : null} Excel
              </button>
            </div>
          </div>
        ))}
        {!items.length && <div className="col-span-full text-center text-slate-400 italic py-8 text-sm">No reports configured.</div>}
      </div>
    </div>
  );
}


export default FuelDashboard;
